import { TOKENS } from '../../tokens.js';
import {
  createRangeReader,
  createSettleGate,
  type FetchLike,
  FRAME_SANDBOX,
  frameDocument,
  readWithRetry,
  type SheetModel,
  sheetsFromWorkbook,
  sheetTableHtml,
  ViewerError,
} from './viewer-core.js';

/**
 * Önizleyicinin çizim motorları (plan M8a Task 15): PDF pdf.js ile, DOCX docx-preview ile, XLSX
 * exceljs ile. Kütüphaneler ve pdf.js worker'ı uygulamayla paketlenir ve ilk kullanımda yüklenir;
 * CDN ve internet gerekmez [sorular #20]. Bu modül yalnız tarayıcıda çalışır.
 */

/** CSS px / PDF noktası (96 / 72): %100'de A4 genişliği 794 px. */
const PDF_SCALE = 96 / 72;

export interface PdfHandle {
  numPages: number;
  /** Sayfanın %100'deki boyutu (CSS px). */
  pageSize(page: number): Promise<{ width: number; height: number }>;
  /**
   * Sayfayı tuvale çizer; `scale` CSS px ölçeği, çözünürlük `pixelRatio` ile. Aynı tuvalde yeni
   * çizimden önce `cancel` çağrılır (pdf.js aynı tuvale iki çizimi kabul etmez).
   */
  render(
    page: number,
    canvas: HTMLCanvasElement,
    scale: number,
    pixelRatio: number,
  ): { promise: Promise<void>; cancel: () => void };
  /** Belge içi aramada kullanılan sayfa metni. */
  text(page: number): Promise<string>;
  /** Dosya ucunun `x-felix-revision-state` başlığı (inceleme WF-1). */
  servedState: string | null;
  destroy(): void;
}

/**
 * pdf.js ve worker'ı (uygulamayla paketli, ilk kullanımda yüklenir). Eski (legacy) derleme: yeni
 * JS özellikleri (ör. `Map.getOrInsertComputed`) için dolgulu; gemideki Electron ve ofis
 * tarayıcıları aynı kodu çalıştırır. Tek worker bütün belgeler için (`workerPort`). Uygulama
 * içinde PDF doldurma da bunu kullanır (M8b3; ADR 0042 §19).
 */
export async function pdfjsLib() {
  const [pdfjs, worker] = await Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker'),
  ]);
  if (!pdfjs.GlobalWorkerOptions.workerPort) {
    pdfjs.GlobalWorkerOptions.workerPort = new worker.default();
  }
  return pdfjs;
}

/** Kapatılmakta olan PDF belgeleri: yeni belge bunlar bitince açılır (inceleme F9). */
const closing = createSettleGate();

/**
 * Önceki belgenin kapanması en çok bu kadar beklenir (ms). Kapanma normalde anında biter; süre
 * dolarsa pdf.js worker'ı asılı kalmıştır (`createSettleGate`).
 */
const CLOSE_WAIT_MS = 2000;

/**
 * PDF'i `Range` ile açar: pdf.js veriyi kendi istediği aralıklarla okur (`PDFDataRangeTransport`),
 * ön yükleme kapalıdır; yalnız görünen sayfaların parçaları iner. WebAssembly kapalı: CSP
 * gevşetilmez (JPEG 2000 resimli PDF'lerde resim çizilmeyebilir). Parça okuması yeniden denenir;
 * yine olmazsa belge kapatılır ve hata açılışta fırlatılır, açıldıktan sonra `onFailure`'a
 * bildirilir (pdf.js aralık hatasını kendisi bildiremez; inceleme F1). İptal (`signal`) yüklenen
 * belgeyi de kapatır.
 */
export async function openPdf(
  url: string,
  options: {
    fetch?: FetchLike;
    signal?: AbortSignal;
    onFailure?: (error: unknown) => void;
  } = {},
): Promise<PdfHandle> {
  const pdfjs = await pdfjsLib();
  const reader = createRangeReader(url, options.fetch, {
    chunkSize: 256 * 1024,
    ...(options.signal ? { signal: options.signal } : {}),
  });
  const opened = await reader.open();
  const transport = new pdfjs.PDFDataRangeTransport(opened.length, opened.initialData);
  let loaded = false;
  let destroyed = false;
  let rejectOpen: (error: unknown) => void = () => undefined;
  const failed = new Promise<never>((_, reject) => {
    rejectOpen = reject;
  });
  failed.catch(() => undefined);
  let task: ReturnType<typeof pdfjs.getDocument> | null = null;
  const close = () => {
    if (destroyed || !task) return;
    destroyed = true;
    closing.track(task.destroy());
  };
  const fail = (error: unknown) => {
    if (destroyed) return;
    close();
    if (loaded) options.onFailure?.(error);
    else rejectOpen(error);
  };
  transport.requestDataRange = (begin: number, end: number) => {
    readWithRetry(() => reader.read(begin, end), { signal: options.signal }).then((chunk) => {
      if (!destroyed) transport.onDataRange(begin, chunk);
    }, fail);
  };
  // Paylaşılan worker'da önceki belgenin kapanması beklenir (inceleme F9). Kapanma asılı kaldıysa
  // yeni belge yeni worker'da açılır; eski worker sonlandırılmaz: onda açık başka belge (ör.
  // uygulama içinde doldurulan PDF) çalışmayı sürdürür
  if (!(await closing.within(CLOSE_WAIT_MS))) {
    closing.reset();
    const worker = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker');
    pdfjs.GlobalWorkerOptions.workerPort = new worker.default();
  }
  if (options.signal?.aborted) throw new ViewerError('ABORTED');
  task = pdfjs.getDocument({
    range: transport,
    rangeChunkSize: reader.chunkSize,
    disableAutoFetch: true,
    disableStream: true,
    useWasm: false,
    enableXfa: false,
  });
  options.signal?.addEventListener('abort', close, { once: true });
  let document: Awaited<typeof task.promise>;
  try {
    document = await Promise.race([task.promise, failed]);
  } catch (error) {
    close();
    throw error;
  }
  loaded = true;
  return {
    numPages: document.numPages,
    servedState: opened.state,
    async pageSize(page) {
      const proxy = await document.getPage(page);
      const viewport = proxy.getViewport({ scale: PDF_SCALE });
      return { width: viewport.width, height: viewport.height };
    },
    render(page, canvas, scale, pixelRatio) {
      let cancelled = false;
      let task: { cancel: () => void } | null = null;
      const promise = (async () => {
        const proxy = await document.getPage(page);
        if (cancelled) return;
        const viewport = proxy.getViewport({ scale: PDF_SCALE * scale * pixelRatio });
        // Çizim önce görünmeyen tuvalde yapılır; bitince kopyalanır (eski çizim titremez)
        const work = globalThis.document.createElement('canvas');
        work.width = Math.floor(viewport.width);
        work.height = Math.floor(viewport.height);
        const render = proxy.render({ canvas: work, viewport });
        task = render;
        await render.promise;
        if (cancelled) return;
        canvas.width = work.width;
        canvas.height = work.height;
        canvas.getContext('2d')?.drawImage(work, 0, 0);
      })().catch((error: unknown) => {
        if (!cancelled) throw error;
      });
      return {
        promise,
        cancel: () => {
          cancelled = true;
          task?.cancel();
        },
      };
    },
    async text(page) {
      const proxy = await document.getPage(page);
      const content = await proxy.getTextContent();
      return content.items.map((item) => ('str' in item ? item.str : '')).join(' ');
    },
    destroy() {
      close();
    },
  };
}

/** Önizleme iframe'i: sandbox'lı, betiksiz; içerik üst pencereden yazılır. */
export function createFrame(owner: Document, title: string): HTMLIFrameElement {
  const frame = owner.createElement('iframe');
  frame.setAttribute('sandbox', FRAME_SANDBOX);
  frame.setAttribute('title', title);
  frame.setAttribute('referrerpolicy', 'no-referrer');
  frame.srcdoc = frameDocument();
  return frame;
}

/**
 * iframe'in `srcdoc` belgesi yüklenince (sandbox `allow-same-origin`: üst pencere erişir). Eklenir
 * eklenmez görünen geçici `about:blank` belgesi beklenmez; içerik ona yazılırsa kaybolur.
 */
export function frameReady(frame: HTMLIFrameElement): Promise<Document> {
  return new Promise((resolve, reject) => {
    const loaded = () => {
      const document = frame.contentDocument;
      if (document?.body && document.URL === 'about:srcdoc') resolve(document);
      else reject(new ViewerError('FRAME_UNAVAILABLE'));
    };
    if (
      frame.contentDocument?.URL === 'about:srcdoc' &&
      frame.contentDocument.readyState === 'complete'
    ) {
      loaded();
      return;
    }
    frame.addEventListener('load', loaded, { once: true });
  });
}

/** Önizleme belgesinin ortak biçimi (token renkleriyle). */
function frameStyle(document: Document, css: string): void {
  const style = document.createElement('style');
  style.textContent = css;
  document.head.append(style);
}

/**
 * Basılı kopyanın filigranı (her sayfa parçasının `::after`'ı): belgenin CSS'inden sonra ve
 * `!important`; parça kendi yığın bağlamıdır, filigran en üstte (inceleme SEC-1). Yazı tipi yalnız
 * genel `sans-serif`: belgenin gömülü yazı tipi adlı bir aileyi ("Arial") gölgeleyebilir, genel
 * anahtar sözcüğü gölgeleyemez (inceleme M8a3-R1).
 */
export const printWatermarkCss = (text: string): string =>
  `[data-print-root] .wm{position:relative!important;isolation:isolate!important}` +
  `[data-print-root] .wm::after{content:${cssString(text)}!important;display:block!important;visibility:visible!important;position:absolute!important;top:45%!important;left:50%!important;z-index:2147483647!important;transform:translate(-50%,-50%) rotate(-30deg)!important;font:800 72px/1 sans-serif!important;letter-spacing:.08em!important;color:${TOKENS['danger-fg']}!important;opacity:.2!important;pointer-events:none!important;white-space:nowrap!important}`;

/**
 * DOCX'i iframe'e çizer (docx-preview): sayfa kırılımları Word'ün yazdığı kırılımlardan
 * (`ignoreLastRenderedPageBreak: false`); sayfa sayısı yaklaşıktır (ADR 0028 #34 madde 5). Gömülü
 * HTML (altChunk), yorumlar ve izlenen değişiklikler çizilmez; resimler veri adresidir. Durum
 * filigranı iframe'in içine çizilmez: belgenin CSS'i onu gizleyebilirdi; önizleyici üst belgede
 * çizer (inceleme SEC-1).
 */
export async function renderDocx(
  data: ArrayBuffer,
  document: Document,
): Promise<{ pages: number }> {
  const { renderAsync } = await import('docx-preview');
  await renderAsync(data, document.body, document.head, {
    className: 'docx',
    inWrapper: true,
    breakPages: true,
    ignoreLastRenderedPageBreak: false,
    renderHeaders: true,
    renderFooters: true,
    renderFootnotes: true,
    renderEndnotes: true,
    renderAltChunks: false,
    renderComments: false,
    renderChanges: false,
    useBase64URL: true,
    experimental: false,
  });
  frameStyle(
    document,
    // Sayfa çerçeveden genişse sol kenar kesilmez: sarmalayıcı içeriği kadar geniş, ortada
    `.docx-wrapper{display:block;width:max-content;min-width:100%;box-sizing:border-box;background:${TOKENS['viewer-canvas']};padding:24px 16px 8px;}` +
      `.docx-wrapper>section.docx{box-shadow:0 1px 4px rgba(10,31,68,.18);margin:0 auto 24px;}`,
  );
  const pages = document.querySelectorAll('section.docx').length;
  return { pages: Math.max(pages, 1) };
}

/** XLSX'i okur: sayfalar ve hücreler (exceljs; salt okuma). */
export async function readWorkbook(data: ArrayBuffer): Promise<SheetModel[]> {
  const module = await import('exceljs');
  const ExcelJS = (module as unknown as { default?: typeof module }).default ?? module;
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(data);
  return sheetsFromWorkbook(workbook);
}

/**
 * Sayfaları iframe'e yazar; yalnız seçili sayfa görünür. Filigran üst belgede (SEC-1). Sınırı
 * aşan sayfanın üstünde not (`truncatedNote`; inceleme F11).
 */
export function renderSheets(
  document: Document,
  sheets: SheetModel[],
  truncatedNote: (sheet: SheetModel) => string,
) {
  frameStyle(
    document,
    `body{padding:16px;font:12px/1.4 Carlito,Calibri,Arial,sans-serif;}` +
      `.sheet{border-collapse:collapse;background:${TOKENS.surface};table-layout:fixed;}` +
      `.sheet td,.sheet th{border:1px solid ${TOKENS.border};padding:3px 6px;white-space:pre-wrap;overflow-wrap:anywhere;vertical-align:top;}` +
      `.sheet th{background:${TOKENS['table-head-bg']};color:${TOKENS['text-secondary']};font-weight:600;text-align:center;}` +
      `.sheet col.rowhead{width:44px}.sheet .b{font-weight:700}` +
      `[data-sheet][hidden]{display:none}` +
      `.note{margin:0 0 8px;color:${TOKENS['text-secondary']};font-size:12px;}`,
  );
  document.body.innerHTML = sheets
    .map(
      (sheet, index) =>
        `<div data-sheet="${index}"${index === 0 ? '' : ' hidden'}>${sheetTableHtml(sheet, truncatedNote(sheet))}</div>`,
    )
    .join('');
}

/** Basılı kopya için sayfa resmi: PDF sayfası ≈ 150 dpi. */
export async function pdfPageImage(handle: PdfHandle, page: number): Promise<string> {
  const canvas = globalThis.document.createElement('canvas');
  await handle.render(page, canvas, 150 / 96, 1).promise;
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.92),
  );
  if (!blob) throw new ViewerError('PRINT_FAILED');
  return URL.createObjectURL(blob);
}

/**
 * `@page` kenar kutusunda kullanılacak metin: CSS dizesi. Yazdırılabilir ASCII dışındaki her
 * karakter, tırnak ve ters bölü hex kaçışla yazılır (`\\d `): CR, FF ve LF dizeyi bitiremez,
 * altlıktan sonra kural eklenemez (inceleme SEC-2; CSS Syntax §4.3.5).
 */
export function cssString(text: string): string {
  let body = '';
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    body +=
      code >= 0x20 && code <= 0x7e && char !== '"' && char !== '\\'
        ? char
        : `\\${code.toString(16)} `;
  }
  return `"${body}"`;
}

/** Altlığın yazı tipi: yalnız genel aile (gömülü yazı tipi gölgeleyemez; inceleme M8a3-R1). */
const PRINT_FOOTER_FONT = '7pt/1.3 sans-serif';

/** Her sayfanın alt kenar kutusunda altlık (ADR 0028 #30 A); belge `@page` yazamaz (SEC-1). */
export function printPageCss(footer: string): string {
  return `@page{margin:12mm 12mm 22mm!important;@bottom-left{content:${cssString(footer)}!important;font:${PRINT_FOOTER_FONT}!important;color:${TOKENS['navy-900']}!important;vertical-align:top!important;padding-top:4mm!important}}`;
}

/**
 * Basılacak sayfaların kabı (ADR 0028 #30 A): yazdırırken yalnız bu kap görünür; altlık her
 * sayfada (`@page` alt kenar kutusu; Chromium ve Electron). Kenar kutusunu desteklemeyen
 * tarayıcıda her sayfada yinelenen `position: fixed` öğe. Sistemin biçimi (altlık, filigran)
 * içerikten sonra eklenir ve `!important`'tır: belgenin CSS'i onları ezemez (inceleme SEC-1).
 * Dönen işlev kabı ve resimleri kaldırır.
 */
export function mountPrintRoot(
  owner: Document,
  content: (root: HTMLElement) => void,
  footer: string,
  options: { cleanupUrls?: string[]; watermark?: string | null } = {},
): () => void {
  const root = owner.createElement('div');
  root.setAttribute('data-print-root', '');
  root.setAttribute('aria-hidden', 'true');
  const chromium = /Chrome\//.test(owner.defaultView?.navigator.userAgent ?? '');
  const style = owner.createElement('style');
  style.textContent = [
    '[data-print-root]{display:none}',
    options.watermark ? printWatermarkCss(options.watermark) : '',
    '@media print{',
    'body>*:not([data-print-root]){display:none!important}',
    `html,body{background:${TOKENS.surface}!important}`,
    '[data-print-root]{display:block}',
    '[data-print-root] .print-page{break-after:page;break-inside:avoid;display:flex;justify-content:center}',
    '[data-print-root] [data-print-last]{break-after:auto}',
    '[data-print-root] .print-page img{max-width:100%;max-height:250mm;object-fit:contain}',
    '[data-print-root] section.docx{box-shadow:none!important;margin:0!important}',
    `[data-print-root] .print-footer{position:fixed;bottom:0;left:0;right:0;font:${PRINT_FOOTER_FONT};color:${TOKENS['navy-900']}}`,
    '}',
    printPageCss(footer),
  ].join('\n');
  content(root);
  [...root.querySelectorAll('.print-page')].at(-1)?.setAttribute('data-print-last', '');
  // İçerikten (belgenin süzülmüş CSS'inden) sonra: kademede son söz sistemin
  root.append(style);
  if (!chromium) {
    const fixed = owner.createElement('div');
    fixed.className = 'print-footer';
    fixed.textContent = footer;
    root.append(fixed);
  }
  // Ekran okuyucu ve testler altlığı DOM'da da okur
  root.dataset.footer = footer;
  owner.body.append(root);
  return () => {
    root.remove();
    for (const url of options.cleanupUrls ?? []) URL.revokeObjectURL(url);
  };
}
